import { usePostHog } from "posthog-js/react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { z } from "zod";
import {
	getAnalyticsPath,
	limitedAnalyticsEvents,
} from "@/lib/analytics-policy";

export const ANALYTICS_CONSENT_KEY = "analytics-consent";
export const ANALYTICS_CONSENT_AT_KEY = "analytics-consent-at";
export const ANALYTICS_CONSENT_VERSION_KEY = "analytics-consent-version";
export const ANALYTICS_CONSENT_VERSION = "2026-10-12-limited-v1";
export const AnalyticsReadyContext = createContext(false);
/** CNIL guidance: ask again six months after the visitor's choice. */
const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 183;
export const ANALYTICS_CONSENT_EVENT = "analytics-consent-open";
export const ANALYTICS_CONSENT_CHANGED_EVENT = "analytics-consent-changed";

export const ANALYTICS_EVENTS = {
	pageView: "$pageview",
	pageLeave: "$pageleave",
	ctaClick: "cta_click",
	contentView: "content_view",
	contentRead: "content_read",
	contentClick: "content_click",
	sectionView: "section_view",
	localeSwitch: "locale_switch",
	contactSubmit: "contact_submit",
} as const;

export type AnalyticsEventName =
	(typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type PageType =
	| "home"
	| "blog_index"
	| "blog_post"
	| "docs_index"
	| "doc"
	| "service"
	| "use_case_index"
	| "use_case"
	| "work_index"
	| "case_study"
	| "products"
	| "about"
	| "contact"
	| "other";

export type ContentType = "blog" | "doc";

const consentStateSchema = z.enum(["pending", "granted", "denied"]);
export type ConsentState = z.infer<typeof consentStateSchema>;

const notifyConsentChange = () => {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT));
};

export const clearStoredAnalyticsConsent = () => {
	try {
		const keys = [
			ANALYTICS_CONSENT_KEY,
			ANALYTICS_CONSENT_AT_KEY,
			ANALYTICS_CONSENT_VERSION_KEY,
		];
		const hadChoice = keys.some((key) => localStorage.getItem(key) !== null);
		for (const key of keys) localStorage.removeItem(key);
		if (hadChoice) notifyConsentChange();
	} catch {
		// No data can be collected while storage is inaccessible.
	}
};

export const readAnalyticsConsent = (): ConsentState => {
	if (typeof window === "undefined") return "pending";
	try {
		const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
		const parsed = consentStateSchema.safeParse(stored);
		const decidedAt = Number(localStorage.getItem(ANALYTICS_CONSENT_AT_KEY));
		const age = Date.now() - decidedAt;
		const isFresh =
			decidedAt > 0 &&
			Number.isFinite(age) &&
			age >= 0 &&
			age < CONSENT_MAX_AGE_MS;
		const isCurrent =
			localStorage.getItem(ANALYTICS_CONSENT_VERSION_KEY) ===
			ANALYTICS_CONSENT_VERSION;

		if (parsed.success && parsed.data !== "pending" && isFresh && isCurrent) {
			return parsed.data;
		}
		clearStoredAnalyticsConsent();
	} catch {
		// A browser that blocks storage must never implicitly enable analytics.
	}
	return "pending";
};

const storeConsent = (state: "granted" | "denied") => {
	try {
		localStorage.setItem(ANALYTICS_CONSENT_KEY, state);
		localStorage.setItem(ANALYTICS_CONSENT_AT_KEY, String(Date.now()));
		localStorage.setItem(
			ANALYTICS_CONSENT_VERSION_KEY,
			ANALYTICS_CONSENT_VERSION,
		);
	} catch {
		// Without a stored, verifiable choice, collection stays disabled.
	}
};

export const useAnalyticsConsent = () => {
	const [state, setState] = useState<ConsentState | "loading">("loading");

	useEffect(() => {
		if (typeof window === "undefined") return;

		let expiry: ReturnType<typeof setTimeout> | undefined;
		const handleChange = () => {
			clearTimeout(expiry);
			const next = readAnalyticsConsent();
			setState(next);
			if (next === "pending") return;
			const decidedAt = Number(localStorage.getItem(ANALYTICS_CONSENT_AT_KEY));
			// Browsers cap a timeout at about 25 days; recheck during long visits.
			expiry = setTimeout(
				() => {
					handleChange();
					notifyConsentChange();
				},
				Math.min(decidedAt + CONSENT_MAX_AGE_MS - Date.now(), 2_147_483_647),
			);
		};

		handleChange();
		window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleChange);
		window.addEventListener("storage", handleChange);
		window.addEventListener("focus", handleChange);

		return () => {
			clearTimeout(expiry);
			window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleChange);
			window.removeEventListener("storage", handleChange);
			window.removeEventListener("focus", handleChange);
		};
	}, []);

	const grantConsent = () => {
		if (typeof window === "undefined") return;
		storeConsent("granted");
		notifyConsentChange();
	};

	const denyConsent = () => {
		if (typeof window === "undefined") return;
		storeConsent("denied");
		notifyConsentChange();
	};

	return { state, grantConsent, denyConsent };
};

export const useAnalyticsConsentState = () => useAnalyticsConsent().state;

const utmKeys = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
] as const;

export type UtmKey = (typeof utmKeys)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

export const getUtmParams = (search: string): UtmParams => {
	if (!search) return {};
	const query = search.startsWith("?") ? search : `?${search}`;
	const params = new URLSearchParams(query);
	const result: UtmParams = {};

	for (const key of utmKeys) {
		const value = params.get(key);
		if (value) {
			result[key] = value;
		}
	}

	return result;
};

export const normalizePathname = (pathname: string) => {
	if (!pathname || pathname === "/") return "/";
	return pathname.replace(/\/+$/, "");
};

export const getPageContext = (pathname: string) => {
	const normalized = normalizePathname(pathname);

	if (normalized === "/") {
		return { pageType: "home" } as const;
	}
	if (normalized === "/blog") {
		return { pageType: "blog_index", contentType: "blog" } as const;
	}
	if (normalized.startsWith("/blog/")) {
		return {
			pageType: "blog_post",
			contentType: "blog",
			slug: normalized.replace("/blog/", ""),
		} as const;
	}
	if (normalized === "/docs") {
		return { pageType: "docs_index", contentType: "doc" } as const;
	}
	if (normalized.startsWith("/docs/")) {
		return {
			pageType: "doc",
			contentType: "doc",
			slug: normalized.replace("/docs/", ""),
		} as const;
	}

	if (normalized.startsWith("/services/")) {
		return {
			pageType: "service",
			slug: normalized.replace("/services/", ""),
		} as const;
	}
	if (normalized === "/cas-usage") {
		return { pageType: "use_case_index" } as const;
	}
	if (normalized.startsWith("/cas-usage/")) {
		return {
			pageType: "use_case",
			slug: normalized.replace("/cas-usage/", ""),
		} as const;
	}
	if (normalized === "/realisations") {
		return { pageType: "work_index" } as const;
	}
	if (normalized.startsWith("/realisations/")) {
		return {
			pageType: "case_study",
			slug: normalized.replace("/realisations/", ""),
		} as const;
	}
	if (normalized === "/produits") return { pageType: "products" } as const;
	if (normalized === "/a-propos") return { pageType: "about" } as const;
	if (normalized === "/contact") return { pageType: "contact" } as const;

	return { pageType: "other" } as const;
};

export const buildPageViewProperties = ({
	pathname,
	locale,
}: {
	pathname: string;
	locale: string;
}) => ({ path: getAnalyticsPath(pathname), locale });

const omitUndefined = <T extends Record<string, unknown>>(value: T) =>
	Object.fromEntries(
		Object.entries(value).filter(([, entry]) => entry !== undefined),
	) as {
		[K in keyof T]: Exclude<T[K], undefined>;
	};

export const useAnalytics = () => {
	const posthog = usePostHog();
	const ready = useContext(AnalyticsReadyContext);
	const consentState = useAnalyticsConsentState();

	const capture = useCallback(
		(event: AnalyticsEventName, properties?: Record<string, unknown>) => {
			if (!limitedAnalyticsEvents.has(event)) return;
			if (
				!ready ||
				readAnalyticsConsent() !== "granted" ||
				!posthog?.has_opted_in_capturing?.()
			)
				return;
			if (properties) {
				posthog.capture(event, properties);
			} else {
				posthog.capture(event);
			}
		},
		[posthog, ready],
	);

	// Retain hook compatibility without storing campaign profiles or errors.
	const register = useCallback(
		(_properties: Record<string, unknown>) => {},
		[],
	);
	const registerOnce = register;
	const captureException = useCallback(
		(_error: unknown, _properties?: Record<string, unknown>) => {},
		[],
	);

	const canCapture =
		ready &&
		consentState === "granted" &&
		(posthog?.has_opted_in_capturing?.() ?? false);

	return {
		posthog,
		capture,
		register,
		registerOnce,
		captureException,
		canCapture,
	};
};

type SectionTracking = {
	id: string;
	label: string;
};

type SectionTrackingOptions = {
	pageType?: PageType;
	locale?: string;
};

export const useSectionViewTracking = (
	sections: SectionTracking[],
	{ pageType, locale }: SectionTrackingOptions = {},
) => {
	const { capture } = useAnalytics();
	const consentState = useAnalyticsConsentState();
	const sectionMap = useMemo(
		() => new Map(sections.map((section) => [section.id, section])),
		[sections],
	);

	useEffect(() => {
		if (consentState !== "granted") return;
		if (typeof window === "undefined") return;
		if (!("IntersectionObserver" in window)) return;
		if (sectionMap.size === 0) return;

		const seen = new Set<string>();
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					const section = sectionMap.get(entry.target.id);
					if (!section || seen.has(section.id)) continue;

					seen.add(section.id);
					capture(
						ANALYTICS_EVENTS.sectionView,
						omitUndefined({
							section: section.label,
							section_id: section.id,
							page_type: pageType,
							locale,
						}),
					);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.5 },
		);

		for (const { id } of sectionMap.values()) {
			const element = document.getElementById(id);
			if (element) {
				observer.observe(element);
			}
		}

		return () => observer.disconnect();
	}, [capture, consentState, sectionMap, pageType, locale]);
};

type ContentReadTrackingOptions = {
	contentType: "blog" | "doc";
	slug: string;
	title?: string;
	locale?: string;
};

export const useContentReadTracking = ({
	contentType,
	slug,
	title,
	locale,
}: ContentReadTrackingOptions) => {
	const { capture } = useAnalytics();
	const consentState = useAnalyticsConsentState();

	useEffect(() => {
		if (consentState !== "granted") return;
		if (typeof window === "undefined") return;
		if (!slug) return;

		const startedAt = Date.now();
		const tracked = new Set<number>();

		capture(
			ANALYTICS_EVENTS.contentView,
			omitUndefined({
				content_type: contentType,
				slug,
				title,
				locale,
			}),
		);

		const recordProgress = (threshold: number, progress: number) => {
			if (tracked.has(threshold) || progress < threshold) return;
			tracked.add(threshold);
			const elapsedSeconds = Math.round((Date.now() - startedAt) / 1000);

			capture(
				ANALYTICS_EVENTS.contentRead,
				omitUndefined({
					content_type: contentType,
					slug,
					title,
					locale,
					progress: threshold,
					elapsed_seconds: elapsedSeconds,
				}),
			);
		};

		const handleScroll = () => {
			const scrollHeight =
				document.documentElement.scrollHeight - window.innerHeight;
			if (scrollHeight <= 0) return;
			const progress = window.scrollY / scrollHeight;

			recordProgress(0.5, progress);
			recordProgress(1, progress);
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [capture, consentState, contentType, slug, title, locale]);
};
